import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, Icon } from '@glplus/glpweb-dsl';
import { useSetup } from '../lib/SetupContext';

/**
 * SetupBanner (Figma §9) — the orange-gradient "Finish setting up your store"
 * strip pinned at the top of the member portal until the required setup steps
 * are done. "Continue setup" routes to the first incomplete step. Dismissible
 * for the session (it returns next login until setup is finished).
 */
const DISMISS_KEY = 'glp:vendor:setupBannerDismissed';

export default function SetupBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loading, status, progress } = useSetup();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Hide while loading, once required steps are done, for onboarding vendors
  // (they see the §2 Setup/pending screen instead), or if dismissed this session.
  if (loading || progress.allRequiredDone || status === 'onboarding' || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* private mode */
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        background: 'linear-gradient(90deg, #F89A3C 0%, #F2742A 100%)',
        color: '#fff',
        // Full-bleed strip (Figma §9): cancel the main content padding so the
        // banner runs edge-to-edge under the header, content still aligned.
        borderRadius: 0,
        px: { xs: '16px', sm: '32px', lg: '48px' },
        py: 1.75,
        mt: { xs: -3, md: -4 },
        mx: { xs: '-16px', sm: '-32px', lg: '-48px' },
        mb: 3,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: 'rgba(255,255,255,0.25)',
          borderRadius: 999,
          px: 1.25,
          py: 0.4,
          fontSize: '12px',
          fontWeight: 700,
        }}
      >
        {t('setup.progress', { defaultValue: '{{done}} of {{total}}', done: progress.completed, total: progress.total })}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, color: '#fff', lineHeight: 1.25 }}>
          {t('setup.bannerTitle', { defaultValue: 'Finish setting up your store' })}
        </Typography>
        <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.92)', lineHeight: 1.3 }}>
          {t('setup.bannerBody', { defaultValue: 'Add your business details and products, then go live on Grocery List Plus.' })}
        </Typography>
      </Box>
      <Button
        ariaLabel={t('setup.continue', { defaultValue: 'Continue setup' })}
        buttonText={t('setup.continue', { defaultValue: 'Continue setup' })}
        variant="contained"
        onClick={() => navigate('/setup')}
        sx={{ flexShrink: 0, backgroundColor: '#fff', color: '#C2410C', '&:hover': { backgroundColor: '#f3f3f3' } }}
      />
      <Box
        component="button"
        type="button"
        onClick={dismiss}
        aria-label={t('common.dismiss', { defaultValue: 'Dismiss' })}
        sx={{
          flexShrink: 0,
          border: 'none',
          background: 'transparent',
          color: 'rgba(255,255,255,0.9)',
          cursor: 'pointer',
          display: 'inline-flex',
          p: 0.5,
          borderRadius: 1,
          '&:hover': { color: '#fff' },
        }}
      >
        <Icon name="close" size={18} color="white" ariaLabel="" />
      </Box>
    </Box>
  );
}
